class HeaderComponent extends HTMLElement {
  #initialHeaderTop = 0;
  #scrollHandler = null;
  #resizeObserver = null;

  constructor() {
    super();

    this.menuToggle = null;
    this.menuClose = null;
    this.mobileMenuOverlay = null;
    this.mobileMenu = null;

    /* ===== AJOUT OVERLAY DESKTOP ===== */
    this.desktopDropdowns = [];
    this.desktopOverlay = null;
  }

  connectedCallback() {
    this.menuToggle = this.querySelector('[data-ref="menu-toggle"]');
    this.menuClose = this.querySelector('[data-ref="menu-close"]');
    this.mobileMenuOverlay = this.querySelector('[data-ref="mobile-menu-overlay"]');
    this.mobileMenu = this.querySelector('[data-ref="mobile-menu"]');

    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', this.#handleMenuOpen);
    }

    if (this.menuClose) {
      this.menuClose.addEventListener('click', this.#handleMenuClose);
    }

    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.addEventListener('click', this.#handleOverlayClick);
    }

    document.addEventListener('keydown', this.#handleKeyDown);

    /* ===== OVERLAY DESKTOP MEGAMENU ===== */
    this.#createDesktopOverlay();

    this.desktopDropdowns = this.querySelectorAll(
      'dropdown-component.header__menu-item--megamenu'
    );

    this.desktopDropdowns.forEach((dropdown) => {
      dropdown.addEventListener('mouseenter', this.#handleDesktopDropdownOpen);
      dropdown.addEventListener('mouseleave', this.#handleDesktopDropdownClose);
    });
    /* ===================================== */

    this.#initStickyObserver();

    this.updateHeaderOffsetHeight();
    this.#resizeObserver = new ResizeObserver(() => this.updateHeaderOffsetHeight());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (this.menuToggle) {
      this.menuToggle.removeEventListener('click', this.#handleMenuOpen);
    }

    if (this.menuClose) {
      this.menuClose.removeEventListener('click', this.#handleMenuClose);
    }

    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.removeEventListener('click', this.#handleOverlayClick);
    }

    document.removeEventListener('keydown', this.#handleKeyDown);

    this.desktopDropdowns.forEach((dropdown) => {
      dropdown.removeEventListener('mouseenter', this.#handleDesktopDropdownOpen);
      dropdown.removeEventListener('mouseleave', this.#handleDesktopDropdownClose);
    });

    if (this.desktopOverlay) {
      this.desktopOverlay.remove();
    }

    this.#cleanupStickyObserver();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  /* ===== OVERLAY CREATION ===== */
  #createDesktopOverlay() {
    this.desktopOverlay = document.createElement('div');
    this.desktopOverlay.classList.add('desktop-menu-overlay');
    document.body.appendChild(this.desktopOverlay);
  }

  #handleDesktopDropdownOpen = () => {
    this.desktopOverlay.classList.add('is-active');
  };

  #handleDesktopDropdownClose = () => {
    this.desktopOverlay.classList.remove('is-active');
  };
  /* ============================= */

  #handleMenuOpen = () => {
    this.openMobileMenu();
  };

  #handleMenuClose = () => {
    this.closeMobileMenu();
  };

  #handleOverlayClick = (event) => {
    if (event.target === this.mobileMenuOverlay) {
      this.closeMobileMenu();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.mobileMenuOverlay.classList.contains('is-open')) {
      this.closeMobileMenu();
    }
  };

  openMobileMenu() {
    this.mobileMenuOverlay.classList.remove('is-closing');
    this.mobileMenuOverlay.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    setTimeout(() => {
      this.menuClose?.focus();
    }, 150);
  }

  closeMobileMenu() {
    this.mobileMenuOverlay.classList.add('is-closing');

    setTimeout(() => {
      this.mobileMenuOverlay.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
      this.menuToggle?.focus();
    }, 125);
  }

  updateHeaderOffsetHeight = () => {
    this.style.setProperty('--header-offset-height', `${this.offsetHeight}px`);
  };

  #initStickyObserver() {
    const headerGroup = this.closest('.shopify-section-group-header-group');
    if (!headerGroup || getComputedStyle(headerGroup).position !== 'sticky') {
      return;
    }

    document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => {
      if (section === headerGroup) return;
      this.#initialHeaderTop += section.offsetHeight;
    });

    this.#scrollHandler = this.#throttle(() => {
      this.#checkStickyState();
    }, 10);

    window.addEventListener('scroll', this.#scrollHandler, { passive: true });
    this.#checkStickyState();
  }

  #checkStickyState() {
    if (this.#initialHeaderTop === null) return;

    let offset = this.#initialHeaderTop;
    if (offset === 0) offset = 20;

    const isSticky = window.scrollY >= offset;
    this.dataset.stickyState = isSticky ? 'active' : 'inactive';
  }

  #throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  #cleanupStickyObserver() {
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
    this.#initialHeaderTop = null;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
